<template>
  <div>
    <div
      class="select-wrap select-wrap--xs select-wrap--sp select-wrap-nomargin pull-left"
      :class="{ 'select-wrap-error': hasErrors || isIncomplete }"
    >
      <select
        id="edit_profile_form_expected_date_of_birth_year"
        v-model="selectedYear"
        name="edit_profile_form[year_of_birth]"
        data-testid="year"
      >
        <option
          v-for="optionYear in optionYears"
          :key="optionYear"
          :value="optionYear"
          class="date_picker_year_option"
        >
          {{ optionYear }}
        </option>
      </select>
    </div>
    <p class="select-text push-left--sm text--xs push-right--sm">年</p>
    <div
      class="select-wrap select-wrap--mini select-wrap--sp select-wrap-nomargin pull-left"
      :class="{ 'select-wrap-error': hasErrors || isIncomplete }"
    >
      <select
        id="edit_profile_form_expected_date_of_birth_month"
        v-model="selectedMonth"
        name="edit_profile_form[year_of_birth]"
        data-testid="month"
      >
        <option
          v-for="optionMonth in optionMonths"
          :key="optionMonth"
          :value="optionMonth"
          class="date_picker_month_option"
        >
          {{ optionMonth }}
        </option>
      </select>
    </div>
    <p class="select-text push-left--sm text--xs push-right--sm">月</p>
    <div
      class="select-wrap select-wrap--mini select-wrap--sp select-wrap-nomargin pull-left"
      :class="{ 'select-wrap-error': hasErrors || isIncomplete }"
    >
      <select
        id="edit_profile_form_year_of_birth"
        v-model="selectedDay"
        name="edit_profile_form[year_of_birth]"
        data-testid="date"
      >
        <option
          v-for="dateOfMonth in optionDates"
          :key="dateOfMonth"
          :value="dateOfMonth"
          class="date_picker_date_option"
        >
          {{ dateOfMonth }}
        </option>
      </select>
    </div>
    <p class="select-text push-left--sm text--xs">日</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const EMPTY_OPTION = '--' as const;

type OptionValue = number | typeof EMPTY_OPTION;

const dateModel = defineModel<Date | null>('date', {
  default: null,
});

const dayRef = ref<OptionValue>(dateModel.value ? dateModel.value.getDate() : EMPTY_OPTION);
const monthRef = ref<OptionValue>(dateModel.value ? dateModel.value.getMonth() + 1 : EMPTY_OPTION);
const yearRef = ref<OptionValue>(dateModel.value ? dateModel.value.getFullYear() : EMPTY_OPTION);

const selectedDay = computed({
  get: () => dayRef.value,
  set: (value: OptionValue) => {
    setDate({ day: value });
  },
});
const selectedMonth = computed({
  get: () => monthRef.value,
  set: (value: OptionValue) => {
    setDate({ month: value });
  },
});
const selectedYear = computed({
  get: () => yearRef.value,
  set: (value: OptionValue) => {
    setDate({ year: value });
  },
});

function setDate({
  year = yearRef.value,
  month = monthRef.value,
  day = dayRef.value,
}: {
  year?: OptionValue;
  month?: OptionValue;
  day?: OptionValue;
}) {
  const isAllFilled =
    year !== EMPTY_OPTION && month !== EMPTY_OPTION && day !== EMPTY_OPTION;

   if (isAllFilled) {
    const newDate = dayjs
      .utc()
      .year(Number(year))
      .month(Number(month) - 1)
      .date(Number(day))
      .startOf('day')
      .toDate();
    dateModel.value = newDate;
  } else {
    // 中途半端な状態は前の値を破棄して null に倒す
    dateModel.value = null;
  }
  dayRef.value = day;
  monthRef.value = month;
  yearRef.value = year;
}

const { yearsStartAgo, yearsEndIn, hasErrors, allowEmpty } = defineProps({
  yearsStartAgo: { type: Number, required: false, default: 0 },
  yearsEndIn: { type: Number, required: false, default: 0 },
  hasErrors: { type: Boolean, required: false, default: false },
  allowEmpty: { type: Boolean, required: false, default: true },
});

const optionDates = computed(() => {
  let dates: OptionValue[] = [];
  if (allowEmpty) dates.push(EMPTY_OPTION);
  const lastDateOfMonth = calculateLastDateOfMonth();
  if (!lastDateOfMonth) return dates;
  for (let i = 0; i < lastDateOfMonth; i++) {
    dates.push(i + 1);
  }
  return dates;
});

function calculateLastDateOfMonth() {
  if (yearRef.value === EMPTY_OPTION || monthRef.value === EMPTY_OPTION) {
    return null;
  }
  // この時点で数値であることが確定しているので、キャストして渡す
  let lastDateOfMonth = dayjs()
    .year(Number(yearRef.value))
    .month(Number(monthRef.value) - 1)
    .endOf('month')
    .toDate();
  return lastDateOfMonth.getDate();
}

const optionMonths = computed(() => {
  const months: OptionValue[] = allowEmpty ? [EMPTY_OPTION] : [];
  for (let i = 0; i < 12; i++) {
    months.push(i + 1);
  }
  return months;
});

const optionYears = computed(() => {
  const baseYear = dateModel.value ? dateModel.value.getFullYear() : new Date().getFullYear();
  const yearList = Array.from({ length: yearsStartAgo + yearsEndIn + 1 }, (_, k) => baseYear - yearsStartAgo + k);
  return allowEmpty ? [EMPTY_OPTION, ...yearList] : yearList;
});

const isIncomplete = computed(() => {
  const values = [yearRef.value, monthRef.value, dayRef.value];
  const emptyCount = values.filter(v => v === EMPTY_OPTION).length;
  // 全て空でも全て入力済みでもない状態＝不完全
  return emptyCount > 0 && emptyCount < 3;
});
</script>
